import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import {
  FaBolt,
  FaChartLine,
  FaCheck,
  FaDollarSign,
  FaExclamationTriangle,
  FaGem,
  FaMoneyBillWave,
  FaPause,
  FaPlay,
  FaSave,
  FaSun,
  FaTimes
} from 'react-icons/fa';
import {
  MdAutorenew,
  MdCalendarToday,
  MdEventRepeat,
  MdLoop,
  MdSchedule,
  MdSettings,
  MdSmartToy,
  MdToggleOff,
  MdToggleOn,
  MdTrendingUp
} from 'react-icons/md';
import {
  clearExpensesManualTriggerResponse,
  clearManualTriggerResponse,
  manageOngoingContribution,
  triggerExpensesManual,
  triggerManualContribution,
  type AutomationFrequency,
  type OngoingContribution
} from '../../slices/fundManagementInvestment/simulationDashboardSlice';

// Styled Components
const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1)
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: '700px',
  maxHeight: '90vh',
  overflow: 'auto',
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, 
    ${theme.palette.mode === 'dark' ? '#1e293b 0%' : '#f8fafc 0%'}, 
    ${theme.palette.mode === 'dark' ? '#0f172a 100%' : '#e2e8f0 100%'})`,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
      : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'
}));

const GradientHeader = styled(Box)<{ unitType: 'Income' | 'Growth' }>(({ theme, unitType }) => ({
  padding: theme.spacing(1.5),
  background:
    unitType === 'Income'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  color: 'white',
  position: 'relative'
}));

const FrequencyCard = styled(Card)<{ selected: boolean; gradient: string; iconColor: string }>(
  ({ theme, selected, gradient, iconColor }) => ({
    cursor: 'pointer',
    height: 80,
    position: 'relative',
    overflow: 'hidden',
    background: selected ? gradient : theme.palette.background.paper,
    border: selected ? `2px solid ${iconColor}` : `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1.5),
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: selected ? 'translateY(-2px)' : 'translateY(0)',
    boxShadow: selected
      ? `0 8px 20px -5px ${iconColor}40, 0 0 0 1px ${iconColor}20`
      : theme.shadows[1],
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: selected
        ? `0 12px 30px -5px ${iconColor}50, 0 0 0 1px ${iconColor}30`
        : `0 6px 20px -5px ${iconColor}25`,
      borderColor: iconColor
    }
  })
);

const ProfessionalButton = styled(Button)<{ variant?: string; unitType?: 'Income' | 'Growth' }>(
  ({ theme, unitType }) => ({
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(0.875, 2),
    fontSize: '0.813rem',
    background:
      unitType === 'Income'
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    '&:hover': {
      background:
        unitType === 'Income'
          ? 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
          : 'linear-gradient(135deg, #e881f9 0%, #f3495a 100%)',
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[6]
    }
  })
);

const ManualTriggerButton = styled(Button)<{ unitType: 'Income' | 'Growth' }>(
  ({ theme, unitType }) => ({
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(0.875, 2),
    fontSize: '0.813rem',
    background:
      unitType === 'Income'
        ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
        : 'linear-gradient(135deg, #ff6f00 0%, #e65100 100%)',
    color: 'white',
    '&:hover': {
      background:
        unitType === 'Income'
          ? 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)'
          : 'linear-gradient(135deg, #e65100 0%, #d84315 100%)',
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[6]
    },
    '&.Mui-disabled': {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled
    }
  })
);

const ExpenseTriggerButton = styled(Button)<{ unitType: 'Income' | 'Growth' }>(
  ({ theme, unitType }) => ({
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(0.875, 2),
    fontSize: '0.813rem',
    background:
      unitType === 'Income'
        ? 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)'
        : 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
    color: 'white',
    '&:hover': {
      background:
        unitType === 'Income'
          ? 'linear-gradient(135deg, #c2185b 0%, #880e4f 100%)'
          : 'linear-gradient(135deg, #b71c1c 0%, #880e4f 100%)',
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[6]
    },
    '&.Mui-disabled': {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled
    }
  })
);

interface AutomateContributionModalProps {
  open: boolean;
  onClose: () => void;
  unitType: 'Income' | 'Growth';
}

const AutomateContributionModal: React.FC<AutomateContributionModalProps> = ({
  open,
  onClose,
  unitType
}) => {
  const dispatch = useAppDispatch();

  const selectedProjectId = useAppSelector(
    (state) => state.homeTilesServices.selectedProjectId || ''
  );

  const groupId = selectedProjectId;
  const serviceId = 'fundManagementInvestment';
  const apiUnitType: 'incomeUnits' | 'growthUnits' =
    unitType === 'Income' ? 'incomeUnits' : 'growthUnits';

  // Redux state
  const {
    contributionLoading,
    contributionError,
    incomeUnitsConfig,
    growthUnitsConfig,
    manualTriggerLoading,
    manualTriggerError,
    manualTriggerResponse,
    expensesManualTriggerLoading,
    expensesManualTriggerError,
    expensesManualTriggerResponse
  } = useAppSelector((state) => state.simulationDashboard);

  const currentConfig = apiUnitType === 'incomeUnits' ? incomeUnitsConfig : growthUnitsConfig;

  const [amount, setAmount] = useState<string>('');
  const [selectedFrequency, setSelectedFrequency] = useState<keyof AutomationFrequency>('monthly');
  const [isAutomationEnabled, setIsAutomationEnabled] = useState<boolean>(true);

  // Confirmation dialog state
  const [confirmExpenseDialog, setConfirmExpenseDialog] = useState<boolean>(false);

  // Messages
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const frequencyOptions: Array<{
    key: keyof AutomationFrequency;
    label: string;
    description: string;
    icon: React.ReactNode;
    gradient: string;
    iconColor: string;
    textColor: string;
    selectedTextColor: string;
  }> = [
    {
      key: 'daily',
      label: 'Daily',
      description: 'Every day',
      icon: <FaSun size={16} />,
      gradient: 'linear-gradient(135deg, #FFF3C4 0%, #FFCC5C 100%)',
      iconColor: '#FF8F00',
      textColor: '#B8860B',
      selectedTextColor: '#8B4513'
    },
    {
      key: 'weekly',
      label: 'Weekly',
      description: 'Every week',
      icon: <MdEventRepeat size={16} />,
      gradient: 'linear-gradient(135deg, #E1BEE7 0%, #C8A2C8 100%)',
      iconColor: '#8E24AA',
      textColor: '#6A1B9A',
      selectedTextColor: '#4A148C'
    },
    {
      key: 'fortnightly',
      label: 'Fortnightly',
      description: 'Every 2 weeks',
      icon: <MdSchedule size={16} />,
      gradient: 'linear-gradient(135deg, #B3E5FC 0%, #81D4FA 100%)',
      iconColor: '#0277BD',
      textColor: '#01579B',
      selectedTextColor: '#0D47A1'
    },
    {
      key: 'monthly',
      label: 'Monthly',
      description: 'Every month',
      icon: <MdCalendarToday size={16} />,
      gradient: 'linear-gradient(135deg, #C8E6C9 0%, #A5D6A7 100%)',
      iconColor: '#388E3C',
      textColor: '#2E7D32',
      selectedTextColor: '#1B5E20'
    },
    {
      key: 'quarterly',
      label: 'Quarterly',
      description: 'Every 3 months',
      icon: <MdTrendingUp size={16} />,
      gradient: 'linear-gradient(135deg, #FFCDD2 0%, #FFAB91 100%)',
      iconColor: '#F4511E',
      textColor: '#D84315',
      selectedTextColor: '#BF360C'
    },
    {
      key: 'bi-annually',
      label: 'Bi-annually',
      description: 'Every 6 months',
      icon: <FaChartLine size={16} />,
      gradient: 'linear-gradient(135deg, #F8BBD9 0%, #F48FB1 100%)',
      iconColor: '#E91E63',
      textColor: '#C2185B',
      selectedTextColor: '#880E4F'
    },
    {
      key: 'yearly',
      label: 'Yearly',
      description: 'Every year',
      icon: <FaGem size={16} />,
      gradient: 'linear-gradient(135deg, #D1C4E9 0%, #B39DDB 100%)',
      iconColor: '#7B1FA2',
      textColor: '#6A1B9A',
      selectedTextColor: '#4A148C'
    }
  ];

  useEffect(() => {
    if (open && groupId) {
      dispatch(
        manageOngoingContribution({
          action: 'get',
          groupId,
          serviceId,
          unitType: apiUnitType
        })
      );
    }
  }, [open, dispatch, groupId, serviceId, apiUnitType]);

  useEffect(() => {
    if (currentConfig && currentConfig.frequency && !isNaN(currentConfig.contributionAmount)) {
      setAmount(currentConfig.contributionAmount.toString());
      setSelectedFrequency(getSelectedFrequencyFromObject(currentConfig.frequency));
      setIsAutomationEnabled(currentConfig.isEnableAutomate);
    } else {
      setAmount('');
      setSelectedFrequency('monthly');
      setIsAutomationEnabled(true);
    }
  }, [currentConfig]);

  useEffect(() => {
    if (!open) {
      setErrorMessage('');
      setSuccessMessage('');
      setConfirmExpenseDialog(false);
      dispatch(clearManualTriggerResponse());
      dispatch(clearExpensesManualTriggerResponse());
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (manualTriggerResponse) {
      setSuccessMessage(manualTriggerResponse.message || 'Manual contribution trigger successful!');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setSuccessMessage('');
        dispatch(clearManualTriggerResponse());
        timeoutRef.current = null;
      }, 5000);
    }
  }, [manualTriggerResponse, dispatch]);

  useEffect(() => {
    if (expensesManualTriggerResponse) {
      setSuccessMessage(
        expensesManualTriggerResponse.message || 'Monthly expense triggered successfully!'
      );
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setSuccessMessage('');
        dispatch(clearExpensesManualTriggerResponse());
        timeoutRef.current = null;
      }, 5000);
    }
  }, [expensesManualTriggerResponse, dispatch]);

  useEffect(() => {
    if (errorMessage || manualTriggerError || expensesManualTriggerError) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setErrorMessage('');
        timeoutRef.current = null;
      }, 5000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [errorMessage, manualTriggerError, expensesManualTriggerError]);

  const isFirstTime = !currentConfig && !contributionLoading;

  const createFrequencyObject = (selectedKey: keyof AutomationFrequency): AutomationFrequency => {
    const frequency: AutomationFrequency = {
      daily: false,
      weekly: false,
      fortnightly: false,
      monthly: false,
      quarterly: false,
      'bi-annually': false,
      yearly: false
    };
    frequency[selectedKey] = true;
    return frequency;
  };

  const getSelectedFrequencyFromObject = (
    frequency: AutomationFrequency | null | undefined
  ): keyof AutomationFrequency => {
    if (!frequency || typeof frequency !== 'object') {
      return 'monthly';
    }

    try {
      const selected = Object.entries(frequency).find(([_, value]) => value === true);
      return (selected?.[0] as keyof AutomationFrequency) || 'monthly';
    } catch (error) {
      console.error('Error getting selected frequency:', error);
      return 'monthly';
    }
  };

  const handleSaveAutomation = async () => {
    if (amount && groupId && !isNaN(parseFloat(amount))) {
      setErrorMessage('');

      const frequencyObj = createFrequencyObject(selectedFrequency);

      try {
        const payload: any = {
          action: 'update',
          groupId,
          serviceId,
          unitType: apiUnitType,
          isEnableAutomate: isAutomationEnabled,
          frequency: frequencyObj,
          contributionAmount: parseFloat(amount)
        };

        if (currentConfig?.automationId) {
          payload.automationId = currentConfig.automationId;
        }

        const response = await dispatch(manageOngoingContribution(payload)).unwrap();

        if (response.data && response.data.success !== false) {
          onClose();
        }
      } catch (error: any) {
        console.error('Failed to save automation:', error);
        setErrorMessage(error.message || 'Failed to save automation');
      }
    }
  };

  const handleManualTrigger = async () => {
    if (!groupId) {
      setErrorMessage('Group ID is required');
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');

    try {
      await dispatch(
        triggerManualContribution({
          groupId,
          serviceId,
          unitType: apiUnitType
        })
      ).unwrap();
    } catch (error: any) {
      console.error('Failed to trigger manual contribution:', error);
      setErrorMessage(error.message || 'Failed to trigger manual contribution');
    }
  };

  const handleExpensesTriggerClick = () => {
    setConfirmExpenseDialog(true);
  };

  const handleExpensesTriggerConfirm = async () => {
    if (!groupId) {
      setErrorMessage('Group ID is required');
      setConfirmExpenseDialog(false);
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    setConfirmExpenseDialog(false);

    try {
      await dispatch(
        triggerExpensesManual({
          groupId,
          serviceId,
          unitType: apiUnitType
        })
      ).unwrap();
    } catch (error: any) {
      console.error('Failed to trigger monthly expense:', error);
      setErrorMessage(error.message || 'Failed to trigger monthly expense');
    }
  };

  const handleExpensesTriggerCancel = () => {
    setConfirmExpenseDialog(false);
  };

  const formatCurrency = (amount: number) => {
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '$0';
    }

    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 0
      }).format(amount);
    } catch (error) {
      console.error('Error formatting currency:', error);
      return '$0';
    }
  };

  const getContributionAmount = (config: OngoingContribution | null): number => {
    if (
      !config ||
      isNaN(config.contributionAmount) ||
      config.contributionAmount === null ||
      config.contributionAmount === undefined
    ) {
      return 0;
    }
    return config.contributionAmount;
  };

  const handleClose = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setAmount('');
    setSelectedFrequency('monthly');
    setIsAutomationEnabled(true);
    setErrorMessage('');
    setSuccessMessage('');
    setConfirmExpenseDialog(false);
    dispatch(clearManualTriggerResponse());
    dispatch(clearExpensesManualTriggerResponse());

    onClose();
  };

  return (
    <>
      {/* Confirmation Dialog for Expense Trigger */}
      <Dialog
        open={confirmExpenseDialog}
        onClose={handleExpensesTriggerCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FaExclamationTriangle size={24} color="#ff9800" />
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              Confirm Monthly Expense Trigger
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
            Are you sure you want to manually trigger the monthly expense for{' '}
            <strong>{unitType} Units</strong>?
            <br />
            <br />
            This action will process the expense immediately and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleExpensesTriggerCancel}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '0.813rem',
              borderRadius: 1
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExpensesTriggerConfirm}
            variant="contained"
            disabled={expensesManualTriggerLoading}
            startIcon={
              expensesManualTriggerLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <FaMoneyBillWave size={14} />
              )
            }
            sx={{
              textTransform: 'none',
              fontSize: '0.813rem',
              borderRadius: 1,
              background: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #c2185b 0%, #880e4f 100%)'
              }
            }}
          >
            {expensesManualTriggerLoading ? 'Triggering...' : 'Confirm Trigger'}
          </Button>
        </DialogActions>
      </Dialog>

      <AnimatePresence>
        {open && (
          <StyledModal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
              timeout: 500,
              sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.5)' }
            }}
          >
            <Fade in={open}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                style={{ width: '100%', maxWidth: '700px' }}
              >
                <StyledPaper elevation={24}>
                  <GradientHeader unitType={unitType}>
                    <IconButton
                      onClick={handleClose}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'white',
                        zIndex: 1000,
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          transform: 'scale(1.1)'
                        },
                        '&:active': {
                          transform: 'scale(0.95)'
                        },
                        transition: 'all 0.2s ease-in-out',
                        padding: '6px'
                      }}
                    >
                      <FaTimes size={14} />
                    </IconButton>

                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative' }}
                    >
                      <motion.div
                        animate={{
                          rotate: [0, 360],
                          scale: [1, 1.1, 1]
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: 'easeInOut'
                        }}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)'
                        }}
                      >
                        <MdSmartToy size={20} />
                      </motion.div>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: '0.938rem',
                            fontWeight: 700,
                            lineHeight: 1.2,
                            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                        >
                          {unitType} Unit Automation
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.9,
                            fontSize: '0.75rem',
                            textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                          }}
                        >
                          Smart Investment Assistant
                        </Typography>
                      </Box>
                    </Box>
                  </GradientHeader>

                  {/* Alerts */}
                  <AnimatePresence>
                    {(contributionError ||
                      errorMessage ||
                      manualTriggerError ||
                      expensesManualTriggerError) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Box sx={{ p: 1.5 }}>
                          <Alert
                            severity="error"
                            sx={{
                              borderRadius: 1.5,
                              fontSize: '0.75rem',
                              '& .MuiAlert-icon': { fontSize: '1rem' }
                            }}
                            onClose={() => {
                              setErrorMessage('');
                            }}
                          >
                            {contributionError ||
                              errorMessage ||
                              manualTriggerError ||
                              expensesManualTriggerError}
                          </Alert>
                        </Box>
                      </motion.div>
                    )}

                    {successMessage && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Box sx={{ p: 1.5 }}>
                          <Alert
                            severity="success"
                            sx={{
                              borderRadius: 1.5,
                              fontSize: '0.75rem',
                              '& .MuiAlert-icon': { fontSize: '1rem' }
                            }}
                            onClose={() => {
                              setSuccessMessage('');
                              dispatch(clearManualTriggerResponse());
                              dispatch(clearExpensesManualTriggerResponse());
                            }}
                          >
                            {successMessage}
                          </Alert>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Content */}
                  <Box sx={{ p: 2.5 }}>
                    {contributionLoading ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          py: 6
                        }}
                      >
                        <CircularProgress size={36} />
                        <Typography
                          variant="body1"
                          sx={{ ml: 2, color: 'text.secondary', fontSize: '0.875rem' }}
                        >
                          Loading configuration...
                        </Typography>
                      </Box>
                    ) : (
                      <AnimatePresence mode="wait">
                        {isFirstTime ? (
                          <motion.div
                            key="first-time"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                          >
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                              <motion.div
                                animate={{
                                  scale: [1, 1.05, 1],
                                  rotate: [0, 5, -5, 0]
                                }}
                                transition={{
                                  duration: 4,
                                  repeat: Infinity,
                                  ease: 'easeInOut'
                                }}
                                style={{
                                  display: 'inline-flex',
                                  width: 60,
                                  height: 60,
                                  borderRadius: 16,
                                  background:
                                    unitType === 'Income'
                                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                      : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginBottom: '20px',
                                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                                }}
                              >
                                <MdAutorenew size={32} color="white" />
                              </motion.div>

                              <Typography
                                variant="h5"
                                sx={{
                                  fontSize: '1.125rem',
                                  fontWeight: 700,
                                  mb: 2,
                                  background:
                                    unitType === 'Income'
                                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                      : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                  backgroundClip: 'text',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent'
                                }}
                              >
                                Setup Your Automation
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.813rem',
                                  color: 'text.secondary',
                                  mb: 3,
                                  maxWidth: '400px',
                                  margin: '0 auto 20px auto',
                                  lineHeight: 1.5
                                }}
                              >
                                Configure intelligent automated contributions for your{' '}
                                {unitType.toLowerCase()} units with our smart scheduling system.
                              </Typography>
                            </Box>
                          </motion.div>
                        ) : (
                          <motion.div
                            key="current-config"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                          >
                            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <MdLoop size={18} color="#666" />
                              <Typography
                                variant="h6"
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  color: 'text.primary'
                                }}
                              >
                                Current Configuration
                              </Typography>
                            </Box>

                            <Card
                              sx={{
                                background: currentConfig?.isEnableAutomate
                                  ? `linear-gradient(135deg, ${
                                      unitType === 'Income'
                                        ? 'rgba(102, 126, 234, 0.1)'
                                        : 'rgba(240, 147, 251, 0.1)'
                                    } 0%, rgba(255,255,255,0.8) 100%)`
                                  : 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(255,255,255,0.8) 100%)',
                                border: `2px solid ${
                                  currentConfig?.isEnableAutomate
                                    ? unitType === 'Income'
                                      ? '#667eea'
                                      : '#f093fb'
                                    : '#e0e0e0'
                                }`,
                                borderRadius: 2,
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: 3,
                                mb: 3
                              }}
                            >
                              <CardContent sx={{ p: 2.5 }}>
                                <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                                  {currentConfig?.isEnableAutomate ? (
                                    <Chip
                                      icon={<FaPlay size={10} />}
                                      label="Active"
                                      sx={{
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.688rem',
                                        height: 24,
                                        '& .MuiChip-icon': { color: 'white' }
                                      }}
                                    />
                                  ) : (
                                    <Chip
                                      icon={<FaPause size={10} />}
                                      label="Paused"
                                      sx={{
                                        backgroundColor: '#ff9800',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.688rem',
                                        height: 24,
                                        '& .MuiChip-icon': { color: 'white' }
                                      }}
                                    />
                                  )}
                                </Box>

                                <Box sx={{ pr: 8 }}>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontSize: '1.5rem',
                                      fontWeight: 800,
                                      mb: 1.5,
                                      background:
                                        unitType === 'Income'
                                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                          : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                      backgroundClip: 'text',
                                      WebkitBackgroundClip: 'text',
                                      WebkitTextFillColor: 'transparent'
                                    }}
                                  >
                                    {formatCurrency(getContributionAmount(currentConfig))}
                                  </Typography>

                                  <Box
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
                                  >
                                    <Chip
                                      label={
                                        currentConfig && currentConfig.frequency
                                          ? getSelectedFrequencyFromObject(currentConfig.frequency)
                                          : 'Not set'
                                      }
                                      sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        textTransform: 'capitalize',
                                        backgroundColor:
                                          unitType === 'Income' ? '#e3f2fd' : '#fce4ec',
                                        color: unitType === 'Income' ? '#1976d2' : '#c2185b',
                                        height: 28,
                                        px: 1.5
                                      }}
                                    />
                                  </Box>

                                  {/* Manual Trigger Buttons */}
                                  <Grid container spacing={2} sx={{ mt: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                      <ManualTriggerButton
                                        variant="contained"
                                        onClick={handleManualTrigger}
                                        disabled={manualTriggerLoading || !groupId}
                                        startIcon={
                                          manualTriggerLoading ? (
                                            <CircularProgress size={14} color="inherit" />
                                          ) : (
                                            <FaBolt size={14} />
                                          )
                                        }
                                        unitType={unitType}
                                        fullWidth
                                        sx={{ fontSize: '0.75rem', py: 1 }}
                                      >
                                        {manualTriggerLoading
                                          ? 'Triggering...'
                                          : 'Trigger Contribution'}
                                      </ManualTriggerButton>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <ExpenseTriggerButton
                                        variant="contained"
                                        onClick={handleExpensesTriggerClick}
                                        disabled={expensesManualTriggerLoading || !groupId}
                                        startIcon={
                                          expensesManualTriggerLoading ? (
                                            <CircularProgress size={14} color="inherit" />
                                          ) : (
                                            <FaMoneyBillWave size={14} />
                                          )
                                        }
                                        unitType={unitType}
                                        fullWidth
                                        sx={{ fontSize: '0.75rem', py: 1 }}
                                      >
                                        {expensesManualTriggerLoading
                                          ? 'Triggering...'
                                          : 'Trigger Monthly Expense'}
                                      </ExpenseTriggerButton>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </CardContent>
                            </Card>
                          </motion.div>
                        )}

                        {/* Configuration Form */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.2 }}
                        >
                          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <MdSettings size={18} color="#666" />
                            <Typography
                              variant="h6"
                              sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}
                            >
                              {isFirstTime ? 'Setup Configuration' : 'Update Configuration'}
                            </Typography>
                          </Box>

                          {/* Amount Input */}
                          <Box sx={{ mb: 3.5 }}>
                            <TextField
                              fullWidth
                              label="Contribution Amount"
                              type="number"
                              value={amount}
                              onChange={(e) => setAmount(e.target.value)}
                              disabled={contributionLoading}
                              variant="outlined"
                              inputProps={{ min: 0, step: 1 }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <FaDollarSign size={14} color="#666" />
                                  </InputAdornment>
                                ),
                                sx: { fontSize: '0.875rem' }
                              }}
                              InputLabelProps={{
                                sx: { fontSize: '0.875rem' }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  height: 46,
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: unitType === 'Income' ? '#667eea' : '#f093fb',
                                    borderWidth: 2
                                  }
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: unitType === 'Income' ? '#667eea' : '#f093fb'
                                }
                              }}
                            />
                          </Box>

                          {/* Frequency Selection */}
                          <Box sx={{ mb: 3.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <MdEventRepeat size={16} color="#666" />
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontSize: '0.813rem',
                                  fontWeight: 600,
                                  color: 'text.primary'
                                }}
                              >
                                Select Frequency
                              </Typography>
                            </Box>

                            <Grid container spacing={2}>
                              {frequencyOptions.map((option) => (
                                <Grid item xs={12} sm={6} md={4} key={option.key}>
                                  <motion.div whileTap={{ scale: 0.97 }} whileHover={{ y: -2 }}>
                                    <FrequencyCard
                                      selected={selectedFrequency === option.key}
                                      gradient={option.gradient}
                                      iconColor={option.iconColor}
                                      onClick={() => setSelectedFrequency(option.key)}
                                    >
                                      <CardContent
                                        sx={{
                                          p: 1.5,
                                          textAlign: 'center',
                                          height: '100%',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          '&:last-child': { pb: 1.5 },
                                          position: 'relative'
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            color:
                                              selectedFrequency === option.key
                                                ? option.selectedTextColor
                                                : option.iconColor,
                                            mb: 0.75,
                                            filter:
                                              selectedFrequency === option.key
                                                ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
                                                : 'none'
                                          }}
                                        >
                                          {option.icon}
                                        </Box>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontSize: '0.75rem',
                                            fontWeight: 700,
                                            color:
                                              selectedFrequency === option.key
                                                ? option.selectedTextColor
                                                : option.textColor,
                                            textShadow:
                                              selectedFrequency === option.key
                                                ? '0 1px 2px rgba(0,0,0,0.1)'
                                                : 'none',
                                            mb: 0.25
                                          }}
                                        >
                                          {option.label}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: '0.688rem',
                                            color:
                                              selectedFrequency === option.key
                                                ? `${option.selectedTextColor}CC`
                                                : `${option.textColor}AA`,
                                            fontWeight: 500
                                          }}
                                        >
                                          {option.description}
                                        </Typography>

                                        {selectedFrequency === option.key && (
                                          <motion.div
                                            initial={{ scale: 0, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{
                                              type: 'spring',
                                              stiffness: 500,
                                              damping: 30
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                background: option.iconColor,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                                              }}
                                            >
                                              <FaCheck size={10} color="white" />
                                            </Box>
                                          </motion.div>
                                        )}
                                      </CardContent>
                                    </FrequencyCard>
                                  </motion.div>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>

                          {/* Enable/Disable Toggle */}
                          <Box sx={{ mb: 3.5 }}>
                            <Card
                              sx={{
                                p: 2.5,
                                backgroundColor: 'background.default',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontSize: '0.813rem',
                                      fontWeight: 600,
                                      color: isAutomationEnabled ? 'text.primary' : 'text.secondary'
                                    }}
                                  >
                                    Automation {isAutomationEnabled ? 'Enabled' : 'Disabled'}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.688rem',
                                      color: 'text.secondary',
                                      mt: 0.25
                                    }}
                                  >
                                    {isAutomationEnabled
                                      ? 'Automation will run as scheduled'
                                      : 'Automation is paused'}
                                  </Typography>
                                </Box>

                                <Switch
                                  checked={isAutomationEnabled}
                                  onChange={(e) => setIsAutomationEnabled(e.target.checked)}
                                  size="medium"
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#4caf50'
                                    },
                                    '& .MuiSwitch-track': { borderRadius: 11 },
                                    '& .MuiSwitch-thumb': { borderRadius: 8 }
                                  }}
                                />
                              </Box>
                            </Card>
                          </Box>

                          {/* Action Buttons */}
                          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <ProfessionalButton
                              variant="contained"
                              onClick={handleSaveAutomation}
                              disabled={
                                contributionLoading ||
                                !amount ||
                                !groupId ||
                                isNaN(parseFloat(amount)) ||
                                parseFloat(amount) <= 0
                              }
                              startIcon={
                                contributionLoading ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <FaSave size={14} />
                                )
                              }
                              unitType={unitType}
                              sx={{ px: 4, py: 1.25, fontSize: '0.813rem' }}
                            >
                              {contributionLoading ? 'Saving...' : 'Save Configuration'}
                            </ProfessionalButton>
                          </Box>
                        </motion.div>
                      </AnimatePresence>
                    )}
                  </Box>
                </StyledPaper>
              </motion.div>
            </Fade>
          </StyledModal>
        )}
      </AnimatePresence>
    </>
  );
};

export default AutomateContributionModal;
